import { IconButton, Typography } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { buttonMessages } from "@saleor/intl";
import { DialogProps } from "@saleor/types";
import classNames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ConfirmButton, {
  ConfirmButtonTransitionState
} from "../ConfirmButton/ConfirmButton";

const useStyles = makeStyles(
  theme => ({
    actions: (props: ActionDialogVariantProps) => ({
      borderTop: "none",
      display: "flex",
      justifyContent: props.buttonsAlignment || "center",
      marginBottom: 14
    }),
    cancelButton: {
      backgroundColor: "#F4F4F4",
      outline: `1px solid`,
      padding: "6px 16px",
      width: 136
    },
    closeIcon: {
      margin: 10,
      padding: 5,
      position: "absolute",
      right: 0,
      top: 0
    },
    confirmButton: (props: ActionDialogVariantProps) => ({
      "&:hover": {
        backgroundColor: "#ACCAFF"
      },
      "&:hover .MuiButton-label": {
        color: "#0030FF"
      },
      backgroundColor: props.disabled ? "#B0B0B0" : "#0030FF",
      borderRadius: 6,
      color: "#FFFFFF",
      height: 39,
      padding: "6px 30px",
      width: 136
    }),
    content: {
      padding: "0 1em",
      textAlign: "center"
    },
    defaultText: {
      color: "#000000",
      fontSize: "1em",
      fontWeight: 800,
      marginTop: "1em",
      textAlign: "center"
    },
    deleteButton: {
      "& span": {
        "& span": {
          color: "#002972"
        }
      },
      "&:hover": {
        backgroundColor: theme.palette.error.main
      },
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    dialog: (props: ActionDialogVariantProps) => ({
      borderRadius: 10,
      position: "relative",
      right: 0,
      top: 0,
      width: props.dialogWidth || "35em"
    }),
    dialogButtons: (props: ActionDialogVariantProps) => ({
      justifyContent: props.buttonsAlignment
    }),
    fullWidthButtons: {
      display: "flex",
      ...{
        "& button": {
          flex: "1"
        }
      }
    },
    singleButton: {
      justifyContent: "center"
    },
    title: (props: ActionDialogVariantProps) => ({
      border: "none",
      display: props.titleAlignment === "start" ? "flex" : "center",
      fontWeight: "bold",
      textAlign: "center"
    }),
    titleText: {
      color: "#EC5D77",
      fontSize: "1em",
      fontWeight: 800,
      marginTop: "1em",
      textAlign: "center"
    }
  }),
  { name: "ActionDialogVariant" }
);

export type ButtonKeys = keyof typeof buttonMessages;

export interface ActionDialogVariantProps extends DialogProps {
  actionBlocking?: boolean;
  buttonsAlignment?: "center" | "flex-end" | "flex-start";
  hideCancelButton?: boolean;
  children?: React.ReactNode;
  subContent?: React.ReactNode;
  closeButtonLabel?: ButtonKeys | null;
  confirmButtonLabel?: string;
  confirmKeepLabel?: boolean;
  confirmButtonState: ConfirmButtonTransitionState;
  fullWidthButtons?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  warningTitle?: string;
  defaultTitle?: string;
  variant?: "default" | "delete" | "info";
  disabled?: boolean;
  titleAlignment?: "start" | "center";
  dialogWidth?: string;
  onConfirm();
}

const ActionDialogVariant: React.FC<ActionDialogVariantProps> = props => {
  const {
    actionBlocking,
    children,
    subContent,
    closeButtonLabel,
    confirmButtonLabel,
    confirmKeepLabel,
    confirmButtonState,
    open,
    warningTitle,
    defaultTitle,
    variant,
    disabled,
    onConfirm,
    onClose,
    hideCancelButton
  } = props;

  const classes = useStyles(props);
  const intl = useIntl();

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      classes={{
        paper: classes.dialog
      }}
    >
      <DialogTitle className={classes.title}>
        <IconButton className={classes.closeIcon} onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Typography
          className={warningTitle ? classes.titleText : classes.defaultText}
        >
          {warningTitle || defaultTitle}
        </Typography>
      </DialogTitle>
      <Typography className={classes.content}>{children}</Typography>
      <Typography>{subContent}</Typography>
      <DialogActions className={classes.actions}>
        {hideCancelButton ? null : (
          <Button
            id={closeButtonLabel ? `${closeButtonLabel}Btn` : "cancelDialogBtn"}
            className={classes.cancelButton}
            disabled={actionBlocking && confirmButtonState === "loading"}
            onClick={onClose}
          >
            {closeButtonLabel ? (
              <FormattedMessage {...buttonMessages[closeButtonLabel]} />
            ) : (
              <FormattedMessage {...buttonMessages.cancel} />
            )}
          </Button>
        )}
        {variant !== "info" && (
          <ConfirmButton
            id={
              confirmButtonLabel
                ? `${confirmButtonLabel}Btn`
                : variant === "delete"
                ? "deleteDialogBtn"
                : "confirmDialogBtn"
            }
            transitionState={confirmButtonState}
            disabled={disabled || confirmButtonState === "loading"}
            keepLabel={confirmKeepLabel}
            variant="contained"
            onClick={onConfirm}
            className={classNames({
              [classes.confirmButton]: variant !== "delete",
              [classes.deleteButton]: variant === "delete"
            })}
          >
            <div className={classes.confirmButton}>
              {confirmButtonLabel ||
                (variant === "delete"
                  ? intl.formatMessage(buttonMessages.delete)
                  : intl.formatMessage(buttonMessages.confirm))}
            </div>
          </ConfirmButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

ActionDialogVariant.defaultProps = {
  actionBlocking: false,
  buttonsAlignment: "flex-end",
  closeButtonLabel: null,
  confirmKeepLabel: false,
  fullWidthButtons: false,
  maxWidth: "xs",
  variant: "default"
};
ActionDialogVariant.displayName = "ActionDialogVariant";
export default ActionDialogVariant;
